import {Injectable, PLATFORM_ID, REQUEST, inject} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class TerritoryService {
  private static readonly ACTIVE_TERRITORY_STORAGE_KEY = 'xplorer.activeTerritory';
  private readonly platformId = inject(PLATFORM_ID);
  private readonly request = inject(REQUEST, { optional: true }) as any;

  constructor() { }

  getTerritoryCodeFromUrl(): string {
    const fallbackTerritoryCode = this.getFallbackTerritoryCode();

    if (isPlatformBrowser(this.platformId)) {
      return TerritoryService.resolveCurrentTerritoryCode(fallbackTerritoryCode);
    }

    const requestHostname = this.getHostnameFromRequest(this.request);
    if (!requestHostname) {
      return fallbackTerritoryCode;
    }

    return TerritoryService.resolveTerritoryCodeFromHostname(requestHostname, fallbackTerritoryCode);
  }

  setActiveTerritory(territoryCode: string): void {
    TerritoryService.persistActiveTerritoryCode(territoryCode);
  }

  static resolveCurrentTerritoryCode(fallbackTerritoryCode: string = 'mugello'): string {
    if (typeof window === 'undefined') {
      return fallbackTerritoryCode;
    }

    if (environment.IS_LOCAL) {
      return TerritoryService.getStoredActiveTerritoryCode(fallbackTerritoryCode);
    }

    return TerritoryService.resolveTerritoryCodeFromHostname(window.location.hostname, fallbackTerritoryCode);
  }

  static persistActiveTerritoryCode(territoryCode: string): void {
    if (!environment.IS_LOCAL || typeof window === 'undefined' || !territoryCode) {
      return;
    }

    const normalizedTerritoryCode = territoryCode.toLowerCase();
    if (!TerritoryService.isKnownTerritoryCode(normalizedTerritoryCode)) {
      return;
    }

    try {
      window.localStorage.setItem(TerritoryService.ACTIVE_TERRITORY_STORAGE_KEY, normalizedTerritoryCode);
    } catch {
      // Ignore local storage issues in restricted browsing modes.
    }
  }

  static addTerritoryCodeToUrl(originalUrl: string, newSubdomain: string): string {
    if (!originalUrl || !newSubdomain) {
      throw new Error('Invalid URL or subdomain');
    }

    const parsedUrl = new URL(originalUrl);
    const hostnameParts = parsedUrl.hostname.split('.');

    if (hostnameParts.length >= 3) {
      hostnameParts[0] = newSubdomain;
      parsedUrl.hostname = hostnameParts.join('.');
    } else {
      parsedUrl.hostname = `${newSubdomain}.${parsedUrl.hostname}`;
    }

    return parsedUrl.toString();
  }

  static resolveTerritoryCodeFromHostname(hostname: string, fallbackTerritoryCode: string = 'mugello'): string {
    if (!hostname) {
      return fallbackTerritoryCode;
    }

    const normalizedHost = hostname.toLowerCase();

    if (normalizedHost === 'localhost' || normalizedHost === '127.0.0.1') {
      return fallbackTerritoryCode;
    }

    const hostParts = normalizedHost.split('.');
    if (hostParts.length < 3) {
      return fallbackTerritoryCode;
    }

    const candidate = hostParts[0];
    if (!candidate || candidate === 'www') {
      return fallbackTerritoryCode;
    }

    return candidate;
  }

  private static getStoredActiveTerritoryCode(fallbackTerritoryCode: string): string {
    try {
      const storedTerritoryCode = window.localStorage.getItem(TerritoryService.ACTIVE_TERRITORY_STORAGE_KEY);
      if (storedTerritoryCode) {
        const normalizedStoredTerritoryCode = storedTerritoryCode.toLowerCase();
        if (TerritoryService.isKnownTerritoryCode(normalizedStoredTerritoryCode)) {
          return normalizedStoredTerritoryCode;
        }
      }
    } catch {
      // Ignore local storage issues in restricted browsing modes.
    }

    return fallbackTerritoryCode;
  }

  private static isKnownTerritoryCode(territoryCode: string): boolean {
    const availableTerritories = environment?.AVAILABLE_TERRITORY ?? [];
    return availableTerritories.some(
      (territory) => territory?.code?.toLowerCase?.() === territoryCode
    );
  }

  private getFallbackTerritoryCode(): string {
    return environment?.AVAILABLE_TERRITORY?.[0]?.code || 'mugello';
  }

  private getHostnameFromRequest(request: any): string | null {
    if (!request?.headers) {
      return null;
    }

    const forwardedHost = request.headers['x-forwarded-host'];
    const directHost = request.headers['host'];
    const rawHost = (Array.isArray(forwardedHost) ? forwardedHost[0] : forwardedHost)
      ?? (Array.isArray(directHost) ? directHost[0] : directHost);

    if (!rawHost || typeof rawHost !== 'string') {
      return null;
    }

    return rawHost.split(':')[0].toLowerCase();
  }
}
